/**
 * EPUB — reading (§8 Phase 2, §16).
 *
 * An EPUB is a ZIP of XHTML, so most of it is already built: the archive
 * reader, the relationship resolution, the XML escaping. What is specific to
 * this format is two things, and both are about refusing.
 *
 * **DRM is detected and not circumvented.** An Adobe ADEPT or LCP file is
 * identified and explained, and that is where it stops. Working around it
 * would be illegal in most of the fifteen markets this ships to, and §19's
 * rule about not overstating support cuts the other way here too — a file the
 * app will not open must say so plainly rather than fail obscurely.
 *
 * **Scripts never run.** EPUB 3 permits JavaScript in content documents, and a
 * book is exactly the kind of file a person opens without thinking. The markup
 * is sanitised at parse time — script elements, event handlers, and anything
 * that would reach the network are removed before the text is ever assembled
 * (§30).
 *
 * Read-only, and the catalog says so. A format whose reflow is defined by the
 * reading system has no faithful round trip through a text edit, and offering
 * one would be the §19 mistake.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { hasPart, openPackage, readPart, readPartText } from '../ooxml/package.js';
import type { OpcPackage } from '../ooxml/package.js';
import { normalizePartPath } from '../docx/document-xml.js';
import { unescapeXml } from '../ooxml/xml.js';

const CONTAINER = 'META-INF/container.xml';
const PREVIEW_CHAPTERS = 3;

export interface EpubChapter {
  /** Spine position, which is reading order. */
  readonly index: number;
  readonly part: string;
  /** Title from the navigation document, or the document's own `<title>`. */
  readonly title: string;
  /** Sanitised block text, one entry per paragraph-level element. */
  readonly blocks: readonly EpubBlock[];
  readonly wordCount: number;
}

export interface EpubBlock {
  readonly kind: 'heading' | 'paragraph' | 'quote' | 'list-item' | 'image';
  readonly level: number;
  readonly text: string;
  /** For an image block, the package part it resolves to. */
  readonly part: string | null;
  readonly alt: string;
}

export interface EpubMetadata {
  readonly title: string;
  readonly creators: readonly string[];
  readonly language: string;
  readonly publisher: string | null;
  readonly published: string | null;
  readonly identifier: string | null;
  readonly description: string | null;
}

export interface EpubModel {
  readonly metadata: EpubMetadata;
  readonly chapters: readonly EpubChapter[];
  /** Cover image part, when the package names one. */
  readonly coverPart: string | null;
  readonly images: ReadonlyMap<string, { bytes: Uint8Array; contentType: string }>;
  readonly hasScripts: boolean;
  readonly wordCount: number;
}

/* ------------------------------------------------------------------ *
 * DRM
 * ------------------------------------------------------------------ */

/**
 * Is this book encrypted?
 *
 * `META-INF/encryption.xml` is where a reading system is told which resources
 * are encrypted, so its presence is the signal — and the *absence* of a
 * matching decryption key is not something to work around.
 *
 * Font obfuscation is deliberately not treated as DRM: it uses the same file
 * and is a permitted, non-protective transformation of embedded fonts. A book
 * whose only encrypted resources are fonts reads perfectly well.
 */
function drmState(pkg: OpcPackage): 'none' | 'fonts-only' | 'protected' {
  if (!hasPart(pkg, 'META-INF/encryption.xml')) {
    // The other tell: Adobe's rights file, which has no `encryption.xml` in
    // some older packages.
    return hasPart(pkg, 'META-INF/rights.xml') ? 'protected' : 'none';
  }
  let xml = '';
  try {
    xml = readPartText(pkg, 'META-INF/encryption.xml');
  } catch {
    return 'protected';
  }
  const algorithms = [...xml.matchAll(/Algorithm="([^"]+)"/g)].map((m) => m[1]!);
  const obfuscationOnly = algorithms.length > 0 && algorithms.every((a) =>
    a.includes('embedded-font') || a.includes('idpf.org/2008/embedding'));
  return obfuscationOnly ? 'fonts-only' : 'protected';
}

/* ------------------------------------------------------------------ *
 * package structure
 * ------------------------------------------------------------------ */

interface Opf {
  readonly dir: string;
  readonly metadata: EpubMetadata;
  /** Manifest id → href, resolved to package paths. */
  readonly hrefById: Map<string, string>;
  readonly mediaById: Map<string, string>;
  /** Spine, in reading order. */
  readonly spine: string[];
  readonly coverPart: string | null;
  readonly navPart: string | null;
}

/** The OPF's path, from `META-INF/container.xml`. */
function rootfilePath(pkg: OpcPackage): string {
  if (!hasPart(pkg, CONTAINER)) {
    throw new HandlerError('error.damagedOrIncomplete', `missing ${CONTAINER}`);
  }
  const xml = readPartText(pkg, CONTAINER);
  const path = /<rootfile\b[^>]*\bfull-path="([^"]+)"/.exec(xml)?.[1];
  if (!path) {
    throw new HandlerError('error.damagedOrIncomplete', 'container.xml names no rootfile');
  }
  return normalizePartPath(unescapeXml(path));
}

function attr(tag: string, name: string): string | null {
  const found = new RegExp(`\\b${name}="([^"]*)"`).exec(tag);
  return found ? unescapeXml(found[1]!) : null;
}

/** Text of the first `<dc:name>` element, namespace prefix or not. */
function dc(xml: string, name: string): string | null {
  const found = new RegExp(`<(?:dc:)?${name}\\b[^>]*>([\\s\\S]*?)</(?:dc:)?${name}>`).exec(xml);
  return found ? unescapeXml(found[1]!).trim() || null : null;
}

function readOpf(pkg: OpcPackage, path: string): Opf {
  const xml = readPartText(pkg, path);
  const slash = path.lastIndexOf('/');
  const dir = slash < 0 ? '' : `${path.slice(0, slash)}/`;

  const hrefById = new Map<string, string>();
  const mediaById = new Map<string, string>();
  const propertiesById = new Map<string, string>();

  for (const match of xml.matchAll(/<item\b[^>]*\/?>/g)) {
    const tag = match[0];
    const id = attr(tag, 'id');
    const href = attr(tag, 'href');
    if (!id || !href) continue;
    // Hrefs are relative to the OPF's own folder, and can climb out of it.
    hrefById.set(id, normalizePartPath(`${dir}${href.split('#')[0]!}`));
    mediaById.set(id, attr(tag, 'media-type') ?? '');
    propertiesById.set(id, attr(tag, 'properties') ?? '');
  }

  const spine: string[] = [];
  const spineBlock = /<spine\b[^>]*>([\s\S]*?)<\/spine>/.exec(xml)?.[1] ?? '';
  for (const match of spineBlock.matchAll(/<itemref\b[^>]*\/?>/g)) {
    const idref = attr(match[0], 'idref');
    if (!idref) continue;
    // `linear="no"` marks auxiliary content — notes, adverts. Reading order is
    // what the spine's linear items say, so those are left out.
    if (attr(match[0], 'linear') === 'no') continue;
    const href = hrefById.get(idref);
    if (href && pkg.entryByName.has(href)) spine.push(href);
  }

  // The cover is named two different ways depending on the EPUB version.
  const coverMetaId = /<meta\b[^>]*\bname="cover"[^>]*\bcontent="([^"]+)"/.exec(xml)?.[1];
  const coverPropertyId = [...propertiesById]
    .find(([, props]) => props.split(/\s+/).includes('cover-image'))?.[0];
  const coverPart = (coverPropertyId && hrefById.get(coverPropertyId))
    ?? (coverMetaId && hrefById.get(coverMetaId))
    ?? null;

  const navId = [...propertiesById]
    .find(([, props]) => props.split(/\s+/).includes('nav'))?.[0];

  return {
    dir,
    metadata: {
      title: dc(xml, 'title') ?? '제목 없음',
      creators: [...xml.matchAll(/<(?:dc:)?creator\b[^>]*>([\s\S]*?)<\/(?:dc:)?creator>/g)]
        .map((m) => unescapeXml(m[1]!).trim()).filter(Boolean),
      language: dc(xml, 'language') ?? 'und',
      publisher: dc(xml, 'publisher'),
      published: dc(xml, 'date'),
      identifier: dc(xml, 'identifier'),
      description: dc(xml, 'description'),
    },
    hrefById,
    mediaById,
    spine,
    coverPart,
    navPart: navId ? hrefById.get(navId) ?? null : null,
  };
}

/* ------------------------------------------------------------------ *
 * content documents
 * ------------------------------------------------------------------ */

/**
 * Strip everything executable or remote before reading the text.
 *
 * Done by removal rather than by escaping-on-output: the text extracted here
 * ends up in several places (the reader, search, copy, the outline), and a
 * sanitiser that runs at only one of them protects only that one. §30's rule
 * is that scripts never run, and the surest version of that is that they are
 * not present in the model at all.
 */
function sanitize(xhtml: string): { safe: string; hadScripts: boolean } {
  let hadScripts = /<script\b|\son\w+\s*=|javascript:/i.test(xhtml);
  let safe = xhtml
    .replace(/<script\b[\s\S]*?<\/script>/gi, '')
    .replace(/<style\b[\s\S]*?<\/style>/gi, '')
    // `<object>`, `<embed>` and `<iframe>` can each load something else.
    .replace(/<(object|embed|iframe|audio|video)\b[\s\S]*?<\/\1>/gi, '')
    .replace(/<(object|embed|iframe)\b[^>]*\/?>/gi, '')
    // Inline handlers, in both quoting styles and unquoted.
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*("javascript:[^"]*"|'javascript:[^']*')/gi, '');

  // An epub:switch or a remote stylesheet link contributes nothing to text.
  safe = safe.replace(/<link\b[^>]*\/?>/gi, '');
  if (/<script\b/i.test(safe)) hadScripts = true;
  return { safe, hadScripts };
}

const BLOCK = /<(h[1-6]|p|blockquote|li|div|img|image)\b([^>]*?)(?:\/>|>([\s\S]*?)<\/\1>)/gi;

function textOf(html: string): string {
  return unescapeXml(
    html
      .replace(/<br\b[^>]*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, ''),
  ).replace(/[ \t\r\f\v]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

function parseChapter(
  xhtml: string, part: string, index: number, opf: Opf, maxBlocks: number,
): { chapter: EpubChapter; hadScripts: boolean } {
  const { safe, hadScripts } = sanitize(xhtml);

  const body = /<body\b[^>]*>([\s\S]*?)<\/body>/i.exec(safe)?.[1] ?? safe;
  const blocks: EpubBlock[] = [];
  const dir = part.slice(0, part.lastIndexOf('/') + 1);

  for (const match of body.matchAll(BLOCK)) {
    if (blocks.length >= maxBlocks) break;
    const tag = match[1]!.toLowerCase();
    const attrs = match[2] ?? '';
    const inner = match[3] ?? '';

    if (tag === 'img' || tag === 'image') {
      const src = attr(attrs, 'src') ?? attr(attrs, 'xlink:href') ?? attr(attrs, 'href');
      blocks.push({
        kind: 'image',
        level: 0,
        text: '',
        part: src ? normalizePartPath(`${dir}${src.split('#')[0]!}`) : null,
        alt: attr(attrs, 'alt') ?? '',
      });
      continue;
    }

    const text = textOf(inner);
    if (text === '') continue;

    // A `<div>` that wraps other blocks would otherwise duplicate their text;
    // only leaf divs contribute.
    if (tag === 'div' && /<(p|h[1-6]|blockquote|li)\b/i.test(inner)) continue;

    blocks.push({
      kind: /^h[1-6]$/.test(tag) ? 'heading'
        : tag === 'blockquote' ? 'quote'
          : tag === 'li' ? 'list-item' : 'paragraph',
      level: /^h[1-6]$/.test(tag) ? Number(tag.slice(1)) : 0,
      text,
      part: null,
      alt: '',
    });
  }

  const documentTitle = /<title\b[^>]*>([\s\S]*?)<\/title>/i.exec(safe)?.[1];
  const firstHeading = blocks.find((b) => b.kind === 'heading')?.text;
  const title = (documentTitle ? unescapeXml(documentTitle).trim() : '')
    || firstHeading
    || `${index + 1}부`;

  const wordCount = blocks.reduce((sum, block) => {
    const trimmed = block.text.trim();
    return sum + (trimmed ? trimmed.split(/\s+/).length : 0);
  }, 0);

  return {
    chapter: { index, part, title, blocks, wordCount },
    hadScripts,
  };
}

/* ------------------------------------------------------------------ *
 * navigation
 * ------------------------------------------------------------------ */

/**
 * Chapter titles from the navigation document.
 *
 * The book's own table of contents names its parts better than a heading scrape
 * does — "1장 시작하며" rather than whatever the first `<h2>` happens to say.
 * Both EPUB 3's `nav` and EPUB 2's `toc.ncx` are read, because plenty of files
 * in circulation are still the older shape.
 */
function readNavTitles(pkg: OpcPackage, opf: Opf): Map<string, string> {
  const titles = new Map<string, string>();

  const add = (href: string | null, text: string): void => {
    if (!href || !text.trim()) return;
    const target = normalizePartPath(`${opf.dir}${href.split('#')[0]!}`);
    if (!titles.has(target)) titles.set(target, text.trim());
  };

  if (opf.navPart && hasPart(pkg, opf.navPart)) {
    const navDir = opf.navPart.slice(0, opf.navPart.lastIndexOf('/') + 1);
    const xml = readPartText(pkg, opf.navPart);
    for (const match of xml.matchAll(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi)) {
      const href = attr(match[1]!, 'href');
      if (!href) continue;
      const target = normalizePartPath(`${navDir}${href.split('#')[0]!}`);
      const text = textOf(match[2]!);
      if (text && !titles.has(target)) titles.set(target, text);
    }
  }

  const ncx = [...opf.hrefById.values()].find((h) => h.endsWith('.ncx'));
  if (ncx && hasPart(pkg, ncx)) {
    const xml = readPartText(pkg, ncx);
    for (const match of xml.matchAll(/<navPoint\b[\s\S]*?<\/navPoint>/gi)) {
      const label = /<text\b[^>]*>([\s\S]*?)<\/text>/i.exec(match[0])?.[1];
      const href = /<content\b[^>]*\bsrc="([^"]+)"/i.exec(match[0])?.[1];
      if (label && href) add(href, unescapeXml(label));
    }
  }

  return titles;
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

const IMAGE_TYPES: Readonly<Record<string, string>> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', bmp: 'image/bmp',
};

function readImages(
  pkg: OpcPackage, wanted: ReadonlySet<string>, budgetBytes: number,
): Map<string, { bytes: Uint8Array; contentType: string }> {
  const images = new Map<string, { bytes: Uint8Array; contentType: string }>();
  let spent = 0;
  for (const part of wanted) {
    const extension = part.slice(part.lastIndexOf('.') + 1).toLowerCase();
    const contentType = IMAGE_TYPES[extension];
    // SVG is deliberately excluded: it is a document that can carry script,
    // and rendering one from a book would reopen the hole sanitising closed.
    if (!contentType || contentType === 'image/svg+xml') continue;
    if (!hasPart(pkg, part)) continue;
    try {
      const bytes = readPart(pkg, part);
      if (spent + bytes.length > budgetBytes) continue;
      spent += bytes.length;
      images.set(part, { bytes, contentType });
    } catch { /* an unreadable image is not a reason to refuse the book */ }
  }
  return images;
}

export const epubHandler: FormatHandler<EpubModel> = {
  id: 'epub',
  formatIds: ['epub'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    refuseIfProtected(pkg);

    const opf = readOpf(pkg, rootfilePath(pkg));
    if (opf.spine.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'the spine names no readable documents');
    }

    // §11: the first chapters only. A book's first screen is its first page.
    const titles = readNavTitles(pkg, opf);
    const chapters = opf.spine.slice(0, PREVIEW_CHAPTERS).map((part, index) => {
      const parsed = parseChapter(readPartText(pkg, part), part, index, opf, 60);
      const named = titles.get(part);
      return named ? { ...parsed.chapter, title: named } : parsed.chapter;
    });

    return {
      kind: 'pages',
      unitCount: opf.spine.length,
      unitKey: 'viewer.chapter',
      firstContent: {
        metadata: opf.metadata,
        chapters,
        more: opf.spine.length > PREVIEW_CHAPTERS,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<EpubModel>> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const drm = refuseIfProtected(pkg);

    const opf = readOpf(pkg, rootfilePath(pkg));
    if (opf.spine.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'the spine names no readable documents');
    }

    const titles = readNavTitles(pkg, opf);
    const maxBlocks = Math.max(50, Math.floor(ctx.limits.maxSpreadsheetCells / 200));
    const chapters: EpubChapter[] = [];
    const imageParts = new Set<string>();
    let hasScripts = false;

    for (const [index, part] of opf.spine.entries()) {
      throwIfAborted(ctx.signal);
      const parsed = parseChapter(readPartText(pkg, part), part, index, opf, maxBlocks);
      if (parsed.hadScripts) hasScripts = true;
      const named = titles.get(part);
      chapters.push(named ? { ...parsed.chapter, title: named } : parsed.chapter);
      for (const block of parsed.chapter.blocks) {
        if (block.kind === 'image' && block.part) imageParts.add(block.part);
      }
    }

    if (opf.coverPart) imageParts.add(opf.coverPart);
    const images = readImages(pkg, imageParts, ctx.limits.maxFileBytes / 4);

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (hasScripts) {
      warnings.push({
        messageKey: 'detect.warning.macrosBlocked',
        severity: 'caution',
        detail: 'This book contains scripts. They are removed before the text is read and never run (§30).',
      });
    }
    if (drm === 'fonts-only') {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Embedded fonts are obfuscated, which is not DRM. The text is unaffected; the publisher\'s typefaces are not used.',
      });
    }
    if (opf.metadata.language && opf.metadata.language !== 'und') {
      warnings.push({
        messageKey: 'status.readyToEdit',
        severity: 'info',
        detail: `Book language: ${opf.metadata.language}`,
      });
    }
    if (source.sizeBytes > 50 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large book (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        metadata: opf.metadata,
        chapters,
        coverPart: opf.coverPart,
        images,
        hasScripts,
        wordCount: chapters.reduce((sum, c) => sum + c.wordCount, 0),
      },
      // Read-only by format, not by assessment: a reflowable book has no
      // faithful round trip through a text edit, and the catalog says so.
      readOnly: true,
      editLevel: spec?.edit ?? 'read-only',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },
};

/**
 * Refuse a protected book, in the words §34 asks for.
 *
 * Not "an error occurred": the file is fine, the app is fine, and the reason it
 * will not open is one the user can act on — read it in the application it was
 * bought for. Circumventing the protection is not on the table.
 */
function refuseIfProtected(pkg: OpcPackage): 'none' | 'fonts-only' {
  const state = drmState(pkg);
  if (state === 'protected') {
    throw new HandlerError(
      'error.passwordProtected',
      'this book is DRM-protected; Modu Viewer does not circumvent DRM',
    );
  }
  return state;
}

/* ------------------------------------------------------------------ *
 * reading helpers
 * ------------------------------------------------------------------ */

/** Plain text of the whole book, for search and for copying out. */
export function epubToPlainText(model: EpubModel): string {
  return model.chapters.map((chapter) => {
    const lines = [`— ${chapter.title}`];
    for (const block of chapter.blocks) {
      if (block.kind === 'image') { if (block.alt) lines.push(`[그림] ${block.alt}`); continue; }
      lines.push(block.kind === 'list-item' ? `· ${block.text}` : block.text);
    }
    return lines.join('\n');
  }).join('\n\n');
}

/** Chapter list for the navigation rail. */
export function epubOutline(model: EpubModel): readonly { index: number; title: string; words: number }[] {
  return model.chapters.map((c) => ({ index: c.index, title: c.title, words: c.wordCount }));
}
